import { BreakpointObserver } from '@angular/cdk/layout';
import { STEPPER_GLOBAL_OPTIONS, StepperOrientation } from '@angular/cdk/stepper';
import { Component, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { FormBuilder, Validators } from '@angular/forms';
import { MatStepper } from '@angular/material/stepper';
import { Router } from '@angular/router';
import { AuthService } from 'src/app/auth/auth.service';
import { SppdashboardService } from '../../dashboard/sppdashboard.service';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { distinctUntilChanged, map, Observable } from 'rxjs';
import { faArrowLeft } from '@fortawesome/pro-solid-svg-icons';
import { faInfoCircle } from '@fortawesome/pro-regular-svg-icons';
import { environment } from 'src/environments/env';
import { employmentTypeList, namewithSpace } from 'src/app/utils/constants';
import { UserDataModalComponent } from 'src/app/component/user-data-modal/user-data-modal.component';
import { LiveComponent } from '../../live/live.component';
import Utils from 'src/app/utils/utils';

@Component({
  selector: 'app-iltform',
  standalone: false,
  templateUrl: './iltform.component.html',
  styleUrl: './iltform.component.scss',

  providers: [
    {
      provide: STEPPER_GLOBAL_OPTIONS,
      useValue: { displayDefaultIndicatorType: false, showError: true },
    }
  ]
})
export class ILTFormComponent implements OnInit {
  @ViewChild('stepper') stepper!: MatStepper;
  @ViewChild('stepperLin') stepperLin!: MatStepper;
  public stepperOrientation!: Observable<StepperOrientation>;
  public formSubmitted: boolean = false;

  public horizLinPersonalInfo: any;
  public horizLinFavoriteThings: any;
  disableSubmitButton: boolean = false;
  @Input() isFromRenewal: boolean | null = null;
  public isCheckboxSelected = false;
  public timezoneList: any = [];
  public employmentTypeList: any = employmentTypeList;
  public loggedUserDetails: any;
  public status: any;
  public dialogConfig: any = {};
  public simpleDialog!: MatDialogRef<UserDataModalComponent>;

  @Input() countryList: any;
  @Input() languageList: any;
  @Output() toggleILTSessionList = new EventEmitter<boolean>();
  @Input() fromPortal: string = '';

  icons = {
    faArrowLeft,
    faInfoCircle
  }

  public constructor(
    private readonly _fb: FormBuilder,
    private readonly router: Router,
    breakpointObserver: BreakpointObserver,
    private readonly _auth: AuthService,
    private readonly sppdashboardService: SppdashboardService,
    private readonly dialogModel: MatDialog,
    private readonly _live: LiveComponent,
    private readonly util: Utils,
  ) {
    this.stepperOrientation = breakpointObserver
      .observe('(min-width: 600px)')
      .pipe(map(({ matches }) => (matches ? 'horizontal' : 'vertical')));
  }


  ngOnInit(): void {
    this.getLoggedInUserDetailsSubject();
    this.initializeForms();
  }

  private initializeForms(): void {
    this.horizLinPersonalInfo = this._fb.group({
      firstname: [{ value: '', disabled: true }, [Validators.required, Validators.pattern(namewithSpace)]],
      lastname: [{ value: '', disabled: true }, [Validators.required, Validators.pattern(namewithSpace)]],
      email: [{ value: sessionStorage.getItem('email'), disabled: true }],
      language: [{ value: '', disabled: true }, Validators.required],
      country: [{ value: '', disabled: true }, Validators.required],
      timezone: [{ value: '', disabled: true }, Validators.required],
      employmentType: [{ value: '', disabled: true }, Validators.required],
      alternateEmail: [{ value: '', disabled: true }, [this.util.customEmailValidator]],
    });

    this.horizLinFavoriteThings = this._fb.group({
      mctAgreement: ['', Validators.required],
    });

    const patchableStatuses = ['FIRST_LOGIN', 'OPEN', 'ILT_OPEN', 'EXPIRED', 'ALUMNI'];
    if (patchableStatuses.includes(this.status)) {
      this.onCountryChange({ value: this.loggedUserDetails.country });
      this.horizLinPersonalInfo.patchValue({
        firstname: this.loggedUserDetails.firstname,
        lastname: this.loggedUserDetails.lastname,
        email: this.loggedUserDetails.email,
        language: this.loggedUserDetails.language,
        country: this.loggedUserDetails.country,
        timezone: this.loggedUserDetails.timeZone,
        employmentType: this.loggedUserDetails.employmentType,
        alternateEmail: this.loggedUserDetails.alternateEmail,
      });
    }

    const editableStatuses = ['FIRST_LOGIN', 'OPEN', 'ILT_OPEN', 'EXPIRED', 'ALUMNI'];
    if (editableStatuses.includes(this.status)) {
      this.horizLinPersonalInfo.enable();
      this.horizLinFavoriteThings.enable();
    }

    // email is always read-only; mctAgreement is unlocked only after downloading the doc
    this.horizLinPersonalInfo.get('email')?.disable();
    this.horizLinFavoriteThings.get('mctAgreement')?.disable();
  }


  submit() {
    this.formSubmitted = true;
    this.horizLinPersonalInfo.markAllAsTouched();
    this.horizLinFavoriteThings.markAllAsTouched();

    if (this.formSubmitted && this.horizLinPersonalInfo.valid && this.horizLinFavoriteThings.valid && this.isCheckboxSelected) {
      this.disableSubmitButton = true; // If the form is valid, disable the submit button to prevent multiple submissions

      const payload = {
        firstname: this.horizLinPersonalInfo.value.firstname,
        lastname: this.horizLinPersonalInfo.value.lastname,
        language: this.horizLinPersonalInfo.value.language,
        country: this.horizLinPersonalInfo.value.country,
        timezone: this.horizLinPersonalInfo.value.timezone,
        employmentType: this.horizLinPersonalInfo.value.employmentType,
        alternateEmail: this.horizLinPersonalInfo.value.alternateEmail,
        mctAgreement: this.horizLinFavoriteThings.value.mctAgreement,
      }

      this.sppdashboardService.submitILT(payload, sessionStorage.getItem('email')).subscribe({
        next: (res: any) => {
          if (res.success) {
            this._auth.setLoggedInUserDetails(res.data);
            this.ModalOnSubmitForm(res);
            if (this.fromPortal === 'ALUMNI') {
              this._live.changeLink(res?.data);
            }
          }
        }
      });
    }
  }


  ModalOnSubmitForm(res: any) {

    let resp = {}

    if (res.data.status === 'TEST_OUT_OPEN') {
      res.message = `Thank you for submitting your application.<br/>
                      You may now proceed to the MCT Instructional Skills Assessment. Once payment is completed and the assessment is passed, you can complete your MCT Program enrolment. <br/>
                      For questions, please contact us at <a aria-label="Email MCT Support at mctpvsupport@pearson.com" href="mailto:mctpvsupport@pearson.com">mctpvsupport&#64;pearson.com</a> - we’re here to help!`;
      res.title = 'Your application has been submitted.';
      resp = {
        message: res.message,
        title: res.title,
        data: {
          status: "TEST_OUT_OPEN"
        }
      }
    } else {

      res.message = `Thank you for submitting your application. You may now register for the MCT Onboarding Experience. 
                    Please proceed to the following screen to schedule your training.`;
      res.title = 'Your application has been submitted.';
      resp = {
        message: res.message,
        title: res.title,
        data: {
          status: "FIRST_LOGIN"
        }
      }
    }
    
    this.dialogConfig.value = resp;
    this.simpleDialog = this.dialogModel.open(UserDataModalComponent, { data: this.dialogConfig });
    this.simpleDialog.afterClosed().subscribe({
      next: (result) => {
        if (result) {
          if (result === 'trainingSelection') {
            this.disableSubmitButton = false; // Enable Submit button for any future use without refreshing the page
            // Navigate to training selection page After Alumni To Reinstate flow > 3 years
            if (this.fromPortal === 'ALUMNI') {
              this.router.navigate(['/dashboard'], { state: { skipForm: true } });
            } else {
              this.showILTSessionList();
            }
          }
        }
      },
      error: (error) => {
        console.error('Error in dialog:', error);
      },
      complete: () => {
        console.log('Dialog closed');
      }
    });
  }

  private showILTSessionList(): void {
    this.toggleILTSessionList.emit();
  }


  agrementDoc = () => {
    this.horizLinFavoriteThings.get('mctAgreement')?.enable();
    const pdfUrl = environment.BASE_URL + `/ptp/api/spp/mct/mct_agreement/download`;
    window.open(pdfUrl, '_blank');
  }


  next() {
    this.horizLinPersonalInfo.markAllAsTouched();
    this.horizLinPersonalInfo.updateValueAndValidity();
  }

  previous() {
    this.router.navigate(['/expired-mct']);
  }


  onCountryChange(event: any) {
    this.horizLinPersonalInfo.get('timezone')?.setValue('');
    this.sppdashboardService.getTimeZonesByCountryName(event.value).subscribe({
      next: (response: any) => {
        if (response.success) {
          const timezones = Array.isArray(response.data) ? response.data : [];
          this.timezoneList = timezones;
          return;
        }

        this.timezoneList = [];
      },
      error: (error: any) => {
        this.timezoneList = [];
        console.error("Error fetching Timezone List", error);
      }
    });

  }

  private getLoggedInUserDetailsSubject(): void {
    // Get the Logged in user Details
    this._auth.getLoggedInUserDetails().pipe(
      distinctUntilChanged((prev: any, curr: any) => JSON.stringify(prev) === JSON.stringify(curr))
    )
      .subscribe((res: any) => {
        if (res) {
          this.loggedUserDetails = res;
          this.status = res.status;
        }
      });
  }

}
